import { useEffect, useRef, useState } from 'react'
import { Modal } from '@/common/ui/Modal'
import { Button } from '@/common/ui/Form/Button'
import { openInsufficientBalanceModal } from '@/app/entities/insufficientBalance/model'
import {
    PAYMENT_RESULT_MODAL_ID,
    closePaymentResultModal,
    listenPaymentResultModalOpen,
    type PaymentResultOpenDetail,
} from '../model'
import { PaymentResultStatusIcon } from './PaymentResultStatusIcon'
import './paymentResultModal.css'

const formatAmount = (amount: number) =>
    new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        maximumFractionDigits: 0,
    }).format(amount)

export const PaymentResultModal = () => {
    const [detail, setDetail] = useState<PaymentResultOpenDetail | null>(null)
    const detailRef = useRef<PaymentResultOpenDetail | null>(null)
    const isCloseHandledRef = useRef(false)

    const handleFinalizeClose = () => {
        if (isCloseHandledRef.current) return

        const current = detailRef.current
        if (!current) return

        isCloseHandledRef.current = true
        detailRef.current = null

        if (current.status === 'success') {
            current.onSettledSuccess?.()
            return
        }

        if (current.reopenSource === 'insufficient-balance') {
            openInsufficientBalanceModal({ step: 2 })
        }
    }

    const handleOpen = (nextDetail: PaymentResultOpenDetail) => {
        isCloseHandledRef.current = false
        detailRef.current = nextDetail
        setDetail(nextDetail)

        const modalCheckbox = document.getElementById(PAYMENT_RESULT_MODAL_ID) as HTMLInputElement | null
        if (modalCheckbox) {
            modalCheckbox.checked = true
        }
    }

    const handleClose = () => {
        closePaymentResultModal()
        handleFinalizeClose()
    }

    const handleCloseKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        handleClose()
    }

    useEffect(() => {
        const unsubscribe = listenPaymentResultModalOpen(handleOpen)
        return () => {
            unsubscribe?.()
        }
    }, [])

    useEffect(() => {
        const checkbox = document.getElementById(PAYMENT_RESULT_MODAL_ID) as HTMLInputElement | null
        if (!checkbox) return

        const handleChange = () => {
            if (checkbox.checked) return
            handleFinalizeClose()
        }

        checkbox.addEventListener('change', handleChange)
        return () => {
            checkbox.removeEventListener('change', handleChange)
        }
    }, [])

    const isSuccess = detail?.status === 'success'
    const isFail = detail?.status === 'fail'
    const purchaseDescription =
        detail?.purchaseType === 'tokens'
            ? detail.tokens
                ? `${detail.tokens.toLocaleString('ru-RU')} токенов`
                : detail.purchaseLabel
            : detail?.purchaseLabel

    return (
        <Modal
            id={PAYMENT_RESULT_MODAL_ID}
            className='modal-priority payment-result-modal'
            aria-label={isSuccess ? 'Оплата прошла успешно' : isFail ? 'Оплата не прошла' : 'Результат оплаты'}
            showCloseButton={false}
        >
            {detail && (
                <div className='flex flex-col items-center gap-8 w-full text-center'>
                    <PaymentResultStatusIcon key={`${detail.status}-${detail.purchaseLabel}`} status={detail.status} />

                    <div className='payment-result-content flex flex-col gap-4 w-full'>
                        <h3 className='text-[1.75rem] leading-tight font-medium text-white'>
                            {isSuccess ? 'Оплата прошла успешно' : 'Оплата не прошла'}
                        </h3>

                        <div className='flex flex-col gap-2 text-base text-[#aba7af] leading-[1.4]'>
                            <p>
                                {detail.purchaseType === 'subscription' ? 'Подписка' : 'Покупка токенов'}:{' '}
                                <span className='text-white'>{purchaseDescription}</span>
                            </p>
                            <p>
                                Сумма: <span className='text-white'>{formatAmount(detail.amount)}</span>
                            </p>
                            {isFail && detail.failReason && <p>{detail.failReason}</p>}
                        </div>
                    </div>

                    <div className='payment-result-actions w-full'>
                        <Button
                            size='lg'
                            emphasis='accent'
                            className='w-full'
                            onClick={handleClose}
                            onKeyDown={handleCloseKeyDown}
                            tabIndex={0}
                            aria-label='Закрыть'
                        >
                            Закрыть
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    )
}
